import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslationService } from '../../../../services/translation.service';
import { TranslateDirective } from '../../../../directives/translate.directive';
import { FeatherIconComponent } from '../../feather-icon/feather-icon.component';
import { AuthService } from '../../../../../services/auth.service';

@Component({
  selector: 'app-create-stream-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateDirective, FeatherIconComponent],
  template: `
    <div class="modal-header">
      <h5 class="modal-title">{{ translate('new_stream') }}</h5>
      <button type="button" class="btn-close" [disabled]="isLoading" (click)="activeModal.dismiss()"></button>
    </div>
    <div class="modal-body">
      <form [formGroup]="streamForm" (ngSubmit)="onSubmit()">
        <div class="form-group mb-4">
          <label for="streamTitle" class="form-label">{{ translate('stream_title') }}</label>
          <div class="input-group">
            <span class="input-group-text">
              <i class="fas fa-heading"></i>
            </span>
            <input type="text" 
                   class="form-control" 
                   id="streamTitle" 
                   formControlName="title" 
                   [placeholder]="translate('ENTER_STREAM_TITLE')">
          </div>
        </div>
        
        <div class="form-group mb-4">
          <label class="form-label">Type de live</label>
          <div class="settings-container">
            <div class="form-check mb-2">
              <input class="form-check-input" type="radio" id="streamFree" value="free" formControlName="streamType">
              <label class="form-check-label" for="streamFree">Gratuit</label>
            </div>
            <div class="form-check mb-2">
              <input class="form-check-input" type="radio" id="streamPaid" value="paid" formControlName="streamType">
              <label class="form-check-label" for="streamPaid">Payant</label>
            </div>
            <div class="form-group mt-2" *ngIf="streamForm.get('streamType')?.value === 'paid'">
              <label for="streamPrice" class="form-label">Prix (USD)</label>
              <input type="number" class="form-control" id="streamPrice" formControlName="price"
                     min="0" step="0.01" placeholder="0.00">
            </div>
          </div>
        </div>

        <div class="form-group mb-4">
          <label class="form-label">{{ translate('stream_settings') }}</label>
          <div class="settings-container">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="enableChat" formControlName="enableChat">
              <label class="form-check-label" for="enableChat">
                {{ translate('enable_chat') }}
              </label>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" 
                  class="btn btn-outline-secondary" 
                  [disabled]="isLoading"
                  (click)="activeModal.dismiss()">
            <i class="fas fa-times me-2"></i>{{ translate('cancel') }}
          </button>
          <button type="submit" 
                  class="btn btn-primary" 
                  [disabled]="isLoading">
            <span *ngIf="!isLoading">
              <i class="fas fa-video me-2"></i>{{ translate('start_stream') }}
            </span>
            <div *ngIf="isLoading" class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </button>
        </div>
      </form>
    </div>
  `,
  styles: [`
    .modal-header {
      background-color: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
      padding: 1.25rem 1.5rem;
    }
    
    .modal-title {
      font-weight: 600;
      color: #343a40;
      font-size: 1.25rem;
    }
    
    .modal-body {
      padding: 1.5rem;
    }
    
    .form-label {
      font-weight: 500;
      color: #495057;
      margin-bottom: 0.5rem;
    }
    
    .input-group-text {
      background-color: #f8f9fa;
      border-right: none;
      color: #6c757d;
    }
    
    .form-control {
      border-left: none;
      padding: 0.75rem 1rem;
      font-size: 1rem;
    }
    
    .form-control:focus {
      box-shadow: none;
      border-color: #ced4da;
    }
    
    .input-group:focus-within {
      box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
      border-radius: 0.375rem;
    }
    
    .input-group:focus-within .input-group-text,
    .input-group:focus-within .form-control {
      border-color: #dc3545;
    }
    
    .settings-container {
      background-color: #f8f9fa;
      border-radius: 0.375rem;
      padding: 1.25rem;
      border: 1px solid #e9ecef;
    }
    
    .form-check {
      margin-bottom: 0.75rem;
      padding-left: 1.8rem;
    }
    
    .form-check:last-child {
      margin-bottom: 0;
    }
    
    .form-check-input {
      width: 1.2rem;
      height: 1.2rem;
      margin-left: -1.8rem;
    }
    
    .form-check-input:checked {
      background-color: #dc3545;
      border-color: #dc3545;
    }
    
    .form-check-label {
      font-size: 0.95rem;
      color: #495057;
    }
    
    .modal-footer {
      padding: 1.25rem 1.5rem;
      border-top: 1px solid #e9ecef;
    }
    
    .btn {
      padding: 0.5rem 1.25rem;
      font-weight: 500;
      border-radius: 0.375rem;
    }
    
    .btn-primary {
      background-color: #dc3545;
      border-color: #dc3545;
    }
    
    .btn-primary:hover {
      background-color: #c82333;
      border-color: #bd2130;
    }
    
    .btn-outline-secondary {
      color: #6c757d;
      border-color: #6c757d;
    }
    
    .btn-outline-secondary:hover {
      background-color: #6c757d;
      color: white;
    }
    
    .spinner-border {
      width: 1.2rem;
      height: 1.2rem;
      border-width: 0.15em;
    }
    
    .btn:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }
    
    .form-text {
      font-size: 0.875rem;
      color: #6c757d;
      margin-top: 0.5rem;
    }
  `]
})
export class CreateStreamModalComponent {
  streamForm: FormGroup;
  isLoading = false;
  @Output() submitForm = new EventEmitter<any>();
  @Input() profil: any;

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private translationService: TranslationService,
    private authService: AuthService
  ) {
    this.streamForm = this.formBuilder.group({
      title: new FormControl(''),
      streamType: new FormControl('free'),
      price: new FormControl(0, [Validators.min(0)]),
      enableChat: new FormControl(true),
      enableDonations: new FormControl(true)
    });
  }

  translate(key: string): string {
    return this.translationService.translate(key);
  }

  onSubmit(): void {
    if (this.streamForm.valid) {
      const raw = this.streamForm.getRawValue();
      const isPaid = raw.streamType === 'paid';
      const price = isPaid ? Math.max(0, Number(raw.price) || 0) : 0;
      if (isPaid && price <= 0) {
        return;
      }
      this.isLoading = true;
      const formData = {
        ...raw,
        free: isPaid ? 0 : 1,
        price: price,
      };
      if (!formData.title || String(formData.title).trim() === '') {
        formData.title = `Live de ${this.profil?.scene_name || this.profil?.prenom + ' ' + this.profil?.name}`;
      }
      this.submitForm.emit(formData);
    }
  }

  setLoading(loading: boolean): void {
    this.isLoading = loading;
    if (loading) {
      this.streamForm.disable();
    } else {
      this.streamForm.enable();
    }
  }

  closeModal(stream: any): void {
    this.activeModal.close(stream);
  }
} 